import { DEV_HULL_CODE, DEV_HULL_PASSWORD, LOBBY_COPY, isHullCode, normalizeHullCode, type CreateOptions, type JoinOptions } from "@spacecrew/shared";
import type { ShipState } from "@spacecrew/shared/schema";
import { CloseCode, type Room } from "@colyseus/sdk";
import { client } from "./client.js";
import { ship } from "./ship.svelte.js";
import { wireAudio } from "../audio/wire.svelte.js";
import { gate } from "./gate.svelte.js";
import { mesh } from "../intercom/mesh.svelte.js";
import { closeIntercom } from "../intercom/media.js";

export type SessionStatus = "boot" | "join" | "joining" | "connected" | "reconnecting";

export const session = $state({
  status: "boot" as SessionStatus,
  error: "",
  roomId: "",
  sessionId: "",
  name: "",
  userId: "",
});

let room: Room<any, ShipState> | null = null;
export const getRoom = () => room;

// sessionStorage = tab-scoped: two tabs are two crew members and never steal each other's seat.
const SS = { token: "spacecrew.token", roomId: "spacecrew.roomId" };
// localStorage = browser-scoped: callsign convenience + the guest identity (account seam, Phase 2 replaces it).
const LS = { name: "spacecrew.name", guestId: "spacecrew.guestId" };

/** crypto.randomUUID exists only in secure contexts (https / localhost); plain-http LAN URLs need the fallback. */
function uuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40; b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function guestId(): string {
  let id = localStorage.getItem(LS.guestId);
  if (!id) { id = uuid(); localStorage.setItem(LS.guestId, id); }
  return id;
}

export const rememberedName = () => localStorage.getItem(LS.name) ?? "";

function clearSession() {
  closeIntercom();
  sessionStorage.removeItem(SS.token);
  sessionStorage.removeItem(SS.roomId);
  room = null;
  ship.reset();
  session.roomId = "";
  session.sessionId = "";
}

function wire(r: Room<any, ShipState>) {
  room = r;
  session.roomId = r.roomId;
  session.sessionId = r.sessionId;
  session.userId = guestId();
  session.status = "connected";
  session.error = "";
  sessionStorage.setItem(SS.token, r.reconnectionToken);
  sessionStorage.setItem(SS.roomId, r.roomId);

  r.onStateChange((s) => {
    ship.snap = s.toJSON() as typeof ship.snap;
    // The token rotates on every (re)connection and the SDK fires onReconnect *before* it assigns
    // the new one — so persist it here, after every patch, where it is always current.
    sessionStorage.setItem(SS.token, r.reconnectionToken);
  });
  r.onDrop(() => { session.status = "reconnecting"; });
  r.onReconnect(() => {
    session.status = "connected";
    queueMicrotask(() => sessionStorage.setItem(SS.token, r.reconnectionToken));
  });
  r.onError((code, message) => { console.warn("[room] error", code, message); });
  r.onLeave(async (code) => {
    if (code === CloseCode.CONSENTED) { clearSession(); session.status = "join"; return; }
    // The SDK's automatic retries gave up (or the server closed us). One manual attempt, then re-board.
    const token = sessionStorage.getItem(SS.token);
    if (token) {
      try { wire(await client.reconnect<ShipState>(token)); return; } catch { /* fall through */ }
    }
    clearSession();
    session.error = LOBBY_COPY.expired;
    session.status = "join";
  });
  wireAudio(r);
  mesh.attach(r);
}

function baseOptions(name: string, password: string): JoinOptions {
  const trimmed = name.trim();
  localStorage.setItem(LS.name, trimmed);
  // The dock code rides along on every join: the server re-checks it, gate screen or no gate screen.
  const accessCode = gate.accessCode;
  return { name: trimmed, password, userId: guestId(), ...(accessCode ? { accessCode } : {}) };
}

export async function commission(name: string, password: string, seed?: number) {
  session.status = "joining"; session.error = "";
  try {
    const opts: CreateOptions = { ...baseOptions(name, password), ...(seed !== undefined ? { seed } : {}) };
    wire(await client.create<ShipState>("ship", opts));
  } catch (e) { fail(e); }
}

/** Board by hull code (5 chars, case-insensitive). The code is resolved to a room id server-side. */
export async function board(code: string, name: string, password: string) {
  session.status = "joining"; session.error = "";
  const hull = normalizeHullCode(code);
  if (!isHullCode(hull)) { session.error = LOBBY_COPY.badCode; session.status = "join"; return; }
  try {
    const res = await fetch(`/hull/${hull}`, { headers: gate.headers });
    if (!res.ok) { session.error = res.status === 404 ? LOBBY_COPY.noHull : LOBBY_COPY.badCode; session.status = "join"; return; }
    const { roomId } = (await res.json()) as { roomId: string };
    wire(await client.joinById<ShipState>(roomId, baseOptions(name, password)));
  } catch (e) { fail(e); }
}

/** Development shortcut: board the fixed dev hull, creating it if nobody has yet. One press, no typing. */
export async function quickBoard(name?: string) {
  session.status = "joining"; session.error = "";
  const callsign = (name?.trim() || rememberedName() || `CREW-${Math.floor(10 + Math.random() * 90)}`).slice(0, 16);
  try {
    const res = await fetch(`/hull/${DEV_HULL_CODE}`, { headers: gate.headers });
    if (res.ok) {
      const { roomId } = (await res.json()) as { roomId: string };
      wire(await client.joinById<ShipState>(roomId, baseOptions(callsign, DEV_HULL_PASSWORD)));
    } else {
      const opts: CreateOptions = { ...baseOptions(callsign, DEV_HULL_PASSWORD), devHull: true };
      wire(await client.create<ShipState>("ship", opts));
    }
  } catch (e) { fail(e); }
}

/** Live hulls on this server (local-only phase). */
export async function liveHulls(): Promise<{ hull: string; crew: number }[]> {
  try { const r = await fetch("/hulls", { headers: gate.headers }); return r.ok ? ((await r.json()) as { hulls: { hull: string; crew: number }[] }).hulls : []; } catch { return []; }
}

/** On page load: if this tab had a seat, take it back (browser refresh mid-session). */
export async function resume() {
  const token = sessionStorage.getItem(SS.token);
  if (!token) { session.status = "join"; return; }
  session.status = "joining";
  try { wire(await client.reconnect<ShipState>(token)); }
  catch { clearSession(); session.error = LOBBY_COPY.expired; session.status = "join"; }
}

export async function leave() {
  const r = room;
  clearSession();
  session.status = "join";
  await r?.leave(true);
}

function fail(e: unknown) {
  const msg = e instanceof Error ? e.message : String(e);
  session.error = /REJECTED|CREDENTIALS/i.test(msg) ? LOBBY_COPY.denied : msg.toUpperCase();
  session.status = "join";
}
